import React, { PureComponent } from "react";

import {
  CustomizedXAxisTickTimeDay,
  CustomizedYAxisTick,
  Error,
  selectFormat,
  StandardTooltip,
  isDateFromDataInISOFormat,
} from "./../../../Utils/Utils.js";
import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  // InsightsWithinTooltip,
  // InsightsLineLabel,
  // InsightsTickIndicator,
} from "./../../../Utils/InsightsUtils.js";

import { XRayTooltip } from "./../../../Utils/XRayTooltip/XRayTooltip.js";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  // ReferenceDot,
  // LabelList,
} from "recharts";
import moment from "moment";

import { getXrayTooltipPosition } from "../../../../helpers/DOM";

class Comparison extends PureComponent {
  constructor(props) {
    super(props);
    this.node = React.createRef();
    this.divPosition = React.createRef();
    this.chartRef = React.createRef();

    // Initialize state with data from props
    this.state = {
      widgetsData: {
        hasData: props.data?.hasData || false,
        graph: props.data?.data || [],
      },
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };
  }

  componentDidMount() {
    const { childRef } = this.props;
    if (childRef) {
      childRef(this);
    }

    window.addEventListener("resize", this.handleResize);
    window.addEventListener("click", this.handleClickOutside);

    // Initial position update
    setTimeout(this.handleResize, 500);

    this.fetchInsightsData();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("click", this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    // Handle data updates
    if (prevProps.data !== this.props.data) {
      this.setState({
        widgetsData: {
          hasData: this.props.data?.hasData || false,
          graph: this.props.data?.data || [],
        },
      });
    }
  }

  fetchInsightsData = () => {
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      fetchInsights(data, project, project_id, insightsConfig)
        ?.then((insightsData) => {
          if (insightsData?.length > 0) {
            this.setState({ widgetsInsights: insightsData });
          }
        })
        .catch(console.error);
    }
  };

  handleResize = () => {
    this.closeTooltip();
    if (!this.chartRef.current) return;
    const { containerWidth, containerHeight } = this.chartRef.current.state;
    this.setState({ width: containerWidth, height: containerHeight });
  };

  closeTooltip = () => {
    this.setState({ tooltip: { show: false } });
  };

  handleClickOutside = (e) => {
    if (this.divPosition.current?.contains(e.target)) return;
    this.closeTooltip();
  };

  renderLegends = () => {
    const legends = [
      { key: "This Year", dataKey: "thisYear", color: "#fc6d52", type: "line" },
      { key: "2024", dataKey: "year2024", color: "#fc6d52", type: "area" },
      {
        key: "2023",
        dataKey: "year2023",
        color: "#fc6d52",
        type: "dotted-line",
      },
    ];

    return legends.map((entry, i) => (
      <span
        key={i}
        className="legend mt-2 mt-md-0"
        style={{ opacity: entry.type === "area" ? 0.6 : 1 }}
      >
        <span
          className={`legend__icon ${
            entry.type === "line" || entry.type === "dotted-line"
              ? "legend__icon--line"
              : ""
          }`}
          style={{
            background: entry.type === "area" ? entry.color : "unset",
            borderTop:
              entry.type === "line" || entry.type === "dotted-line"
                ? `1px solid ${entry.color}`
                : "unset",
            borderStyle: entry.type === "dotted-line" ? "dashed" : "solid",
          }}
        />
        <span className="legend__text" style={{ color: entry.color }}>
          {entry.key}
        </span>
      </span>
    ));
  };

  getDateTimeFormat = (data, showYearForDailyFormat = false) => {
    if (!data?.length) return "MM/DD";

    const firstElement = data[0];
    const lastElement = data[data.length - 1];

    // Ensure we're working with valid dates
    const firstDate = moment(
      firstElement.full_date || firstElement.date || firstElement.name
    );
    const lastDate = moment(
      lastElement.full_date || lastElement.date || lastElement.name
    );

    if (!firstDate.isValid() || !lastDate.isValid()) {
      return "MM/DD";
    }

    if (lastDate.diff(firstDate, "days") < 2) {
      return "HH:mm";
    }
    if (lastDate.diff(firstDate, "months") <= 12) {
      return showYearForDailyFormat ? "YYYY/MM/DD" : "MM/DD";
    }
    return "YYYY/MM/DD";
  };

  formatXAxisTick = (value) => {
    if (!value) return "";
    // Ensure we're working with a string
    const dateStr = String(value);
    return moment(dateStr).format(
      this.getDateTimeFormat(this.state.widgetsData.graph, false)
    );
  };

  getExportData = () => {
    const dateTimeFormat = this.getDateTimeFormat(
      this.state.widgetsData.graph,
      true
    ).replace(/\//g, "-");

    const data = this.state.widgetsData.graph
      .filter((d) => d.thisYear || d.year2024 || d.year2023)
      .map((d) => {
        return {
          ...d,
          thisYear_label: "This Year",
          year2024_label: "2024",
          year2023_label: "2023",
          name: moment(d.full_date || d.date || d.name)
            .utc()
            .format(),
        };
      });

    return {
      data: data,
      opts: {
        type: "chart",
        date: {
          name: "name",
          // overwrites the default settings for date only when the date is in ISO format
          defined: isDateFromDataInISOFormat(data),
          format: "YYYY-MM-DDThh:mm:ssZ",
          viewFormat: dateTimeFormat,
        },
        values: ["thisYear", "year2024", "year2023"],
      },
    };
  };

  render() {
    const { widgetsData, widgetsInsights, tooltip } = this.state;

    if (!widgetsData?.hasData || !widgetsData.graph?.length) {
      return <Error minHeight={400} />;
    }

    const preparedData = widgetsData.graph.map((item) => {
      const date = item.full_date || item.date || item.name;
      return {
        ...item,
        name: moment(date).format("YYYY-MM-DD"),
        displayDate: moment(date)
          .utc()
          .format(this.getDateTimeFormat(widgetsData.graph, false)),
        thisYear_label: "This Year",
        year2024_label: "2024",
        year2023_label: "2023",
      };
    });

    return (
      <>
        {this.renderLegends()}
        <div ref={this.divPosition} className="d-flex mt-2">
          <ResponsiveContainer height={300} width="100%">
            <ComposedChart
              ref={this.chartRef}
              data={preparedData}
              margin={{ top: 12, right: 0, bottom: 20, left: 0 }}
              isAnimationActive={false}
              onMouseOver={() => CloseSelectedInsight(this.node)}
              onMouseOut={() => OpenSelectedInsight(this.node, !tooltip.show)}
              onClick={(d) => {
                if (d) {
                  const tooltipData = {
                    ...d,
                    show: true,
                    indicator: "comparison",
                    activeLabel: d.payload?.displayDate,
                  };
                  this.setState({ tooltip: tooltipData });
                } else {
                  this.closeTooltip();
                }
              }}
            >
              <CartesianGrid
                stroke="#FEE5DE"
                strokeDasharray="3 3"
                vertical={false}
              />
              <Area
                type="monotone"
                dataKey="year2024"
                fill="#fc6d52"
                stroke="#fc6d52"
                fillOpacity={0.6}
                strokeWidth={0}
              />
              <Line
                type="monotone"
                dataKey="thisYear"
                stroke="#fc6d52"
                strokeWidth="3"
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="year2023"
                stroke="#fc6d52"
                strokeWidth="2"
                strokeDasharray="5 5"
                dot={false}
                activeDot={{ r: 4 }}
              />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={(props) => {
                  // Format the tick value before passing it to CustomizedXAxisTickTimeDay
                  const formattedValue = this.formatXAxisTick(
                    props.payload.value
                  );
                  return (
                    <CustomizedXAxisTickTimeDay
                      {...props}
                      payload={{ ...props.payload, value: formattedValue }}
                    />
                  );
                }}
                padding={{ left: 30, right: 30 }}
                type="category"
                allowDuplicatedCategory={false}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                mirror={true}
                tick={<CustomizedYAxisTick />}
                padding={{ top: 20, bottom: 20 }}
              />
              {!tooltip.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);

                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          return (
                            <div className="standard-tooltip">
                              {tooltip_data && tooltip_data.displayDate && (
                                <header className="standard-tooltip__header">
                                  <span className="standard-tooltip__key-header">
                                    Date:
                                  </span>
                                  <span className="standard-tooltip__value-header">
                                    {tooltip_data.displayDate}
                                  </span>
                                </header>
                              )}
                              <main className="standard-tooltip__main">
                                {/* This Year */}
                                <div className="standard-tooltip__item">
                                  <span className="standard-tooltip__key">
                                    <span
                                      className="standard-tooltip__icon"
                                      style={{
                                        background: "#fc6d52",
                                        opacity: 1,
                                      }}
                                    ></span>
                                    This Year
                                  </span>
                                  <span className="standard-tooltip__value">
                                    {selectFormat(
                                      "number",
                                      tooltip_data?.thisYear
                                    )}
                                  </span>
                                </div>

                                {/* 2024 */}
                                <div className="standard-tooltip__item">
                                  <span className="standard-tooltip__key">
                                    <span
                                      className="standard-tooltip__icon"
                                      style={{
                                        background: "#fc6d52",
                                        opacity: 0.6,
                                      }}
                                    ></span>
                                    2024
                                  </span>
                                  <span className="standard-tooltip__value">
                                    {selectFormat(
                                      "number",
                                      tooltip_data?.year2024
                                    )}
                                  </span>
                                </div>

                                {/* 2023 */}
                                <div className="standard-tooltip__item">
                                  <span className="standard-tooltip__key">
                                    <span
                                      className="standard-tooltip__icon"
                                      style={{
                                        background: "#fc6d52",
                                        opacity: 0.3,
                                      }}
                                    ></span>
                                    2023
                                  </span>
                                  <span className="standard-tooltip__value">
                                    {selectFormat(
                                      "number",
                                      tooltip_data?.year2023
                                    )}
                                  </span>
                                </div>
                              </main>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  }
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>

          <div className="featured-insight-tooltip" ref={this.node}>
            {widgetsInsights.length > 0 && !tooltip.show && (
              <RenderTooltipInsights data={widgetsInsights} />
            )}
          </div>

          {tooltip.activeCoordinate && (
            <div
              className="xray-tooltip-container"
              style={{
                top: "130px",
                left:
                  tooltip.activeCoordinate.x + 1062 + 220 < this.state.width
                    ? `${tooltip.activeCoordinate.x + 15}px`
                    : `${
                        tooltip.activeCoordinate.x -
                        getXrayTooltipPosition(
                          this.divPosition.current,
                          tooltip.activeCoordinate
                        )
                      }px`,
                opacity: tooltip.show ? 1.0 : 0.0,
                visibility: tooltip.show ? "visible" : "hidden",
              }}
            >
              <XRayTooltip
                data={{
                  ...tooltip,
                  activeLabel: tooltip.activeLabel,
                  activePayload: tooltip.activePayload,
                }}
                close={this.closeTooltip}
                insights={widgetsInsights}
              />
            </div>
          )}
        </div>
      </>
    );
  }
}

export default Comparison;
